// Page Utilisateurs étoffée : Invitations + Comptes existants
import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Plus, Trash2, ShieldCheck, ShieldOff, Mail, Copy, Send, X, Check, Clock } from "lucide-react";
import { api, formatDateTime } from "@/lib/api";
import { useEntity } from "@/lib/entity";
import { useAuth } from "@/lib/auth";
import Modal, { ConfirmDialog } from "@/components/Modal";

const ROLES = ["PDG", "DG", "DAF", "DCM", "RH", "Comptable", "Achats", "Caissier", "Gerant", "Responsable", "user"];

export default function Users() {
  const { user: me } = useAuth();
  const { entities } = useEntity();
  const [tab, setTab] = useState("active");
  const [users, setUsers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [showInvite, setShowInvite] = useState(false);
  const [confirmId, setConfirmId] = useState(null);
  const [confirmInv, setConfirmInv] = useState(null);
  const [inviteResult, setInviteResult] = useState(null);
  const [form, setForm] = useState({ email: "", full_name: "", role: "user", entity_ids: [], send_email: true });

  useEffect(() => { load(); }, []);

  async function load() {
    try {
      const [u, i] = await Promise.all([api.get("/users"), api.get("/invitations")]);
      setUsers(u.data);
      setInvitations(i.data);
    } catch (e) { console.error(e); }
  }

  async function submitInvite(e) {
    e.preventDefault();
    try {
      const { data } = await api.post("/invitations", form);
      setInviteResult(data);
      setForm({ email: "", full_name: "", role: "user", entity_ids: [], send_email: true });
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Erreur");
    }
  }

  const toggleEntity = (id) => {
    setForm((f) => ({ ...f, entity_ids: f.entity_ids.includes(id) ? f.entity_ids.filter(e => e !== id) : [...f.entity_ids, id] }));
  };

  async function toggleActive(u) { await api.patch(`/users/${u.id}`, { active: !u.active }); load(); }
  async function removeUser(id) { await api.delete(`/users/${id}`); load(); }
  async function revokeInvite(id) { await api.delete(`/invitations/${id}`); load(); }

  const copy = (text) => { navigator.clipboard.writeText(text); };

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-6" data-testid="users-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold tracking-tight">Gestion des utilisateurs</h2>
          <p className="text-xs text-obsidian-400 mt-1">{users.length} compte(s) actif(s) · {invitations.filter(i => !i.consumed_at).length} invitation(s) en attente</p>
        </div>
        <button onClick={() => { setShowInvite(true); setInviteResult(null); }} className="btn-primary" data-testid="invite-button">
          <Send size={14} /> Inviter un collaborateur
        </button>
      </div>

      <div className="flex items-center gap-2 border-b border-line">
        {[
          { id: "active", label: "Comptes actifs", count: users.length },
          { id: "invitations", label: "Invitations", count: invitations.filter(i => !i.consumed_at).length },
        ].map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} className={`px-4 h-10 text-sm transition-colors border-b-2 -mb-px ${tab === t.id ? "border-citron text-citron" : "border-transparent text-obsidian-400 hover:text-obsidian-100"}`}>
            {t.label} <span className="ml-1 text-[10px] text-obsidian-500 font-mono">({t.count})</span>
          </button>
        ))}
      </div>

      {tab === "active" && (
        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <thead><tr className="text-obsidian-400 border-b border-line">
              <Th>Nom</Th><Th>Email</Th><Th>Rôle</Th><Th>Entités</Th><Th>2FA</Th><Th>Statut</Th><Th></Th>
            </tr></thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b border-line/50 hover:bg-obsidian-850" data-testid={`user-row-${u.id}`}>
                  <Td className="font-medium">{u.full_name}{u.id === me?.id && <span className="ml-2 text-[10px] text-citron font-mono">(vous)</span>}</Td>
                  <Td className="font-mono text-xs text-obsidian-300">{u.email}</Td>
                  <Td><span className={`pill ${u.role === "PDG" ? "bg-citron text-obsidian-950" : "bg-obsidian-800 text-citron"}`}>{u.role}</span></Td>
                  <Td><span className="text-xs text-obsidian-400">{u.entity_ids?.length === 7 || u.role === "PDG" ? "Toutes" : (u.entity_ids?.join(", ") || "—")}</span></Td>
                  <Td>{u.totp_enabled ? <span className="pill bg-ok/15 text-ok"><ShieldCheck size={10}/> Activé</span> : <span className="pill bg-obsidian-800 text-obsidian-500">Désactivé</span>}</Td>
                  <Td><span className={`pill ${u.active ? "bg-ok/15 text-ok" : "bg-bad/15 text-bad"}`}>{u.active ? "Actif" : "Désactivé"}</span></Td>
                  <Td align="right">
                    {u.id !== me?.id && (
                      <div className="flex justify-end gap-1">
                        <button onClick={() => toggleActive(u)} className="btn-ghost h-7 w-7 p-0" title={u.active ? "Désactiver" : "Activer"}>
                          {u.active ? <ShieldOff size={12} className="text-warn"/> : <ShieldCheck size={12} className="text-ok"/>}
                        </button>
                        {me?.role === "PDG" && (
                          <button onClick={() => setConfirmId(u.id)} className="btn-ghost h-7 w-7 p-0 text-bad"><Trash2 size={12}/></button>
                        )}
                      </div>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "invitations" && (
        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <thead><tr className="text-obsidian-400 border-b border-line">
              <Th>Email</Th><Th>Nom</Th><Th>Rôle</Th><Th>Invité par</Th><Th>Créée</Th><Th>Expire</Th><Th>Statut</Th><Th></Th>
            </tr></thead>
            <tbody>
              {invitations.length === 0 && <tr><td colSpan={8} className="p-12 text-center text-obsidian-500">Aucune invitation</td></tr>}
              {invitations.map((i) => (
                <tr key={i.id} className="border-b border-line/50 hover:bg-obsidian-850">
                  <Td className="font-mono text-xs">{i.email}</Td>
                  <Td>{i.full_name}</Td>
                  <Td><span className="pill bg-obsidian-800 text-citron">{i.role}</span></Td>
                  <Td className="text-xs text-obsidian-400">{i.invited_by_name}</Td>
                  <Td><span className="font-mono text-xs">{formatDateTime(i.created_at)}</span></Td>
                  <Td><span className="font-mono text-xs">{formatDateTime(i.expires_at)}</span></Td>
                  <Td>
                    {i.consumed_at
                      ? <span className="pill bg-ok/15 text-ok"><Check size={10}/> Activée</span>
                      : i.expires_at < new Date().toISOString()
                        ? <span className="pill bg-bad/15 text-bad"><X size={10}/> Expirée</span>
                        : <span className="pill bg-warn/15 text-warn"><Clock size={10}/> En attente</span>}
                  </Td>
                  <Td align="right">
                    {!i.consumed_at && <button onClick={() => setConfirmInv(i.id)} className="btn-ghost h-7 w-7 p-0 text-bad"><Trash2 size={12}/></button>}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Invite Modal */}
      <Modal open={showInvite} onClose={() => { setShowInvite(false); setInviteResult(null); }} title="Inviter un collaborateur" subtitle="Email d'invitation OU mot de passe temporaire" size="lg">
        {!inviteResult ? (
          <form onSubmit={submitInvite} className="space-y-4" data-testid="invite-form">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label block">Nom complet *</label>
                <input required className="input" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} />
              </div>
              <div>
                <label className="label block">Email *</label>
                <input type="email" required className="input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
            </div>
            <div>
              <label className="label block">Rôle</label>
              <select className="input" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                {ROLES.map(r => <option key={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <label className="label block">Entités autorisées</label>
              <div className="grid grid-cols-2 gap-2">
                {entities.filter(e => e.id !== "GROUPE").map((e) => (
                  <label key={e.id} className="flex items-center gap-2 px-3 h-10 bg-obsidian-900 border border-line rounded-sm cursor-pointer hover:border-obsidian-700">
                    <input type="checkbox" checked={form.entity_ids.includes(e.id)} onChange={() => toggleEntity(e.id)} className="accent-citron"/>
                    <span className="text-sm">{e.name}</span>
                  </label>
                ))}
              </div>
            </div>
            <label className="flex items-center gap-2 h-10 px-3 bg-obsidian-900 border border-line rounded-sm cursor-pointer">
              <input type="checkbox" checked={form.send_email} onChange={(e) => setForm({ ...form, send_email: e.target.checked })} className="accent-citron"/>
              <span className="text-sm">Envoyer un email d'invitation (si email configuré, sinon mot de passe temporaire affiché)</span>
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setShowInvite(false)} className="btn-secondary">Annuler</button>
              <button type="submit" className="btn-primary" data-testid="invite-submit"><Send size={14}/> Créer l'invitation</button>
            </div>
          </form>
        ) : (
          <div className="space-y-4">
            <div className="p-3 bg-ok/10 border border-ok/30 rounded-sm">
              <div className="text-ok text-sm font-semibold mb-2">✓ Invitation créée pour {inviteResult.email}</div>
              {inviteResult.email_sent && <div className="text-xs text-obsidian-300">📧 Email d'activation envoyé.</div>}
              {!inviteResult.email_sent && (
                <div className="text-xs text-warn">⚠️ Email non envoyé. Communiquez le mot de passe temporaire OU le lien d'activation ci-dessous au collaborateur.</div>
              )}
            </div>

            <div>
              <label className="label block">Lien d'activation (valide 7 jours)</label>
              <div className="flex gap-2">
                <input readOnly value={inviteResult.activation_link} className="input font-mono text-xs" />
                <button onClick={() => copy(inviteResult.activation_link)} className="btn-secondary h-10 flex-shrink-0" title="Copier"><Copy size={14}/></button>
              </div>
            </div>

            <div>
              <label className="label block">Mot de passe temporaire (fallback option B)</label>
              <div className="flex gap-2">
                <input readOnly value={inviteResult.temp_password} className="input font-mono" />
                <button onClick={() => copy(inviteResult.temp_password)} className="btn-secondary h-10 flex-shrink-0" title="Copier"><Copy size={14}/></button>
              </div>
              <p className="text-[10px] text-obsidian-500 mt-1">⚠️ Note : le mot de passe temporaire ne sert que si l'utilisateur n'utilise pas le lien. Il devrait préférer le lien (plus sûr).</p>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setInviteResult(null)} className="btn-secondary">Nouvelle invitation</button>
              <button onClick={() => { setShowInvite(false); setInviteResult(null); }} className="btn-primary">Fermer</button>
            </div>
          </div>
        )}
      </Modal>

      <ConfirmDialog open={!!confirmId} onClose={() => setConfirmId(null)} onConfirm={() => removeUser(confirmId)} title="Supprimer cet utilisateur ?" message="Action irréversible et tracée." confirmLabel="Supprimer"/>
      <ConfirmDialog open={!!confirmInv} onClose={() => setConfirmInv(null)} onConfirm={() => revokeInvite(confirmInv)} title="Révoquer cette invitation ?" message="L'invitation sera supprimée et le lien deviendra inopérant." confirmLabel="Révoquer"/>
    </motion.div>
  );
}

function Th({ children, align = "left" }) { return <th className={`overline px-4 py-3 text-${align}`}>{children}</th>; }
function Td({ children, align = "left", className = "" }) { return <td className={`px-4 py-3 text-${align} ${className}`}>{children}</td>; }
